import { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-final-form';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getIframelyData } from 'src/app/shared/api/medias';

import type { EmbeddedMediaPreviewProps } from '../../index';

export type EmbeddedMediaPreviewFetchErrorType = 'no_html' | 'api_error';

export const EMBEDDED_MEDIA_PREVIEW_NO_HTML_ERROR = 'no_html';
export const EMBEDDED_MEDIA_PREVIEW_API_ERROR = 'api_error';

const getLinkHref = (text: string): string | undefined => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(text, 'text/html');
	const links = doc.getElementsByTagName('a');
	if (links.length === 0) return undefined;
	return links[0].href;
};

export type UseEmbeddedMediaPreviewResult = ModelHookResult<
	{
		media?: string;
		ariaLabel: string;
	},
	{
		handleClosePreviewClick: () => void;
	}
>;

export function useEmbeddedMediaPreview({
	value,
	onClosePreview,
	onFetchError,
	onFetchSuccess,
}: EmbeddedMediaPreviewProps): UseEmbeddedMediaPreviewResult {
	const { translate } = useI18n();
	const ariaLabel = translate('corgi.petition_update.description.a11y.remove_preview_aria_label');
	/**
	 * `previouslyEnteredUrls` is a set containing urls that have been entered and its preview has been shown.
	 * We use this to ensure that if a preview is removed by the user, we will not preview it again if the same url is entered again.
	 */
	const [previouslyEnteredUrls, setPreviouslyEnteredUrls] = useState(() => new Set());
	const [html, setHtml] = useState<string | undefined>(undefined);
	const form = useForm();
	const utilityContext = useUtilityContext();

	useEffect(() => {
		const fetchData = async (url: string) => {
			try {
				const response = await getIframelyData({ url }, utilityContext);

				if (!response.html) {
					onFetchError && onFetchError(EMBEDDED_MEDIA_PREVIEW_NO_HTML_ERROR);
					return;
				}

				setHtml(response.html);
				form.getFieldState('embeddedMediaUrl')?.change(url);
				setPreviouslyEnteredUrls((prev) => new Set(prev).add(url));
				onFetchSuccess && onFetchSuccess();
			} catch (e) {
				// If the iframely request fails we don't need to show an error message to the user
				// and since we cannot render the embedded media preview, we can just do nothing.
				onFetchError && onFetchError(EMBEDDED_MEDIA_PREVIEW_API_ERROR);
			}
		};

		const embeddedMediaUrl = form.getState().values.embeddedMediaUrl as string;
		if (!embeddedMediaUrl && value) {
			const url = getLinkHref(value);
			if (url && !previouslyEnteredUrls.has(url)) {
				void fetchData(url);
			}
		}
		// We intentionally only want to call this useEffect when the value has changed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, utilityContext]);

	const handleClosePreviewClick = useCallback(() => {
		form.getFieldState('embeddedMediaUrl')?.change(undefined);
		setHtml(undefined);
		onClosePreview && onClosePreview();
	}, [form, setHtml, onClosePreview]);

	return {
		data: {
			media: html,
			ariaLabel,
		},
		actions: {
			handleClosePreviewClick,
		},
	};
}
